<template>
  <div>
    <h6 v-if="isCourier" class="text-muted" v-translate="{step}">Step %{step} of 2</h6>
    <form v-if="step === 1" @submit.stop.prevent="createUser">
      <b-form-row>
        <b-col cols="12">
          <b-form-group>
            <label for="from_contact" v-translate>Name</label>
            <b-form-input id="from_contact" v-model="newUser.name" required />
          </b-form-group>
          <b-form-group>
            <label for="from_email" v-translate>E-mail</label>
            <b-form-input trim id="from_email" v-model="newUser.email" required />
            <b-form-text v-if="isCourier" v-translate="{brandName}">The courier will be pending until %{brandName} approves it. It will receive its password by e-mail once approved.</b-form-text>
            <b-form-text v-else v-translate>An auto-generated password will be emailed to the new user.</b-form-text>
          </b-form-group>
          <b-form-group v-if="isCourier">
            <label for="card_id_number" v-translate>Document Number</label>
            <b-form-input trim id="card_id_number" v-model="documentNumber" required />
            <b-form-text v-translate="{brandName}">%{brandName} needs it to check the courier's background before approving it.</b-form-text>
          </b-form-group>
          <b-form-group v-if="!userType">
            <label for="primary-role" v-translate>Primary Role</label>
            <b-form-select id="primary-role" v-model="primaryRole" :options="primaryRoleSelect" required>
              <template #first>
                <option :value="null" v-translate>-- Select an option --</option>
              </template>
            </b-form-select>
            <b-form-text v-translate>A primary role must be set for the new user.</b-form-text>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-alert variant="info" :show="existingUsers.length > 0">
        <h5 v-translate>Existing Users</h5>
        <p v-translate>There already are users with this e-mail. You may want to look at them (opens in new window):</p>
        <ul>
          <li v-for="(user, index) in existingUsers" :key="index">
            <b-link :to="{name: 'UsersEdit', params: { id: user.id }}" target="_blank">
              {{user.name}}
              (<span v-if="user.active" v-translate>enabled</span><span v-else v-translate>disabled</span>)
            </b-link>
          </li>
        </ul>
      </b-alert>
      <b-form-row>
        <b-col cols="12" class="text-center form-actions">
          <b-form-group>
            <b-button type="submit" variant="secondary" :disabled="saving">
              <b-spinner small v-if="saving"></b-spinner>
              <span v-if="userType === null" v-translate>Create User</span>
              <span v-else v-translate="{roleName: userTypeComputed}">Create %{roleName}</span>
            </b-button>
            <b-button type="button" @click.prevent="emitCancel" variant="outline-secondary" v-translate>Cancel</b-button>
          </b-form-group>
        </b-col>
      </b-form-row>
    </form>
    <div v-else>
      <b-alert variant="success" show>
        <span v-translate="{brandName}">The courier has been created and is pending %{brandName} approval. You can add its documentation now or later from its edit page.</span>
      </b-alert>
      <courier-documentation :user="courier" :prefill="documentationPrefill" @updated="reloadCourier(true)">
        <courier-vehicles :user="courier" @updated="reloadCourier(false)" />
      </courier-documentation>
      <div class="text-center form-actions mt-3">
        <b-button type="button" variant="secondary" @click.prevent="emitSuccess" v-translate>Finish</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  UserService
} from '@/services/api'
import CourierDocumentation from '@/components/Users/CourierDocumentation'
import CourierVehicles from '@/components/Users/CourierVehicles'

const COURIER_INCLUDES = { params: { includes: ['vehicles.vehicle_type'] } }

export default {
  name: 'UsersCreate',
  components: {
    CourierDocumentation,
    CourierVehicles
  },
  props: {
    userType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      newUser: {},
      existingUsers: [],
      primaryRole: null,
      primaryRoleSelect: [],
      // Courier flow: step 1 creates it, step 2 attaches the documentation
      step: 1,
      saving: false,
      documentNumber: '',
      courier: null,
      documentationPrefill: {}
    }
  },
  computed: {
    userTypeComputed() {
      return this.$gettext(this.appRoles.find(x => x.value === this.primaryRole)?.text || this.userType)
    },
    // userType first: primaryRole stays null if appRoles was not loaded on mount, and the courier flow needs no role id
    isCourier() {
      return (this.userType || this.appRoles.find(x => x.value === this.primaryRole)?.text) === this.$roles.COURIER
    }
  },
  watch: {
    primaryRole(val) {
      if (val) {
        this.newUser['x-moova-product'] = window.allowedRoles.find(x => x.role === this.appRoles.find(x => x.value === val).text).product
      }
    }
  },
  mounted () {
    this.primaryRole = this.appRoles.find(x => x.text === this.userType)?.value || null
    this.primaryRoleSelect = this.appRolesTranslated
  },
  methods: {
    createUser() {
      this.existingUsers = []
      // The backend assigns the courier role itself: this branch needs no role id
      if (this.isCourier) {
        this.createCourier()
        return
      }
      const roleId = this.userType
        ? this.appRoles.find(x => x.text === this.userType)?.value
        : this.primaryRole
      if (this.userType && !roleId) {
        this.$root.$children[0].$refs.simplert.openSimplert({
          title: 'ERROR',
          message: this.$gettext('The role for this user is not available yet. Try again in a moment; if it persists, contact support.'),
          type: 'error'
        })
        return
      }
      if (roleId) {
        this.primaryRole = roleId
        const roleName = this.appRoles.find(x => x.value === roleId).text
        this.newUser['x-moova-product'] = window.allowedRoles.find(x => x.role === roleName).product
      }
      this.newUser.country_iso_code = this.profile.company.country_iso_code
      UserService.create(this.newUser)
        .then(response => {
          if (this.userType) {
            UserService.addRole(response.body.id, [roleId])
              .then(() => {
                window.open('/users/edit/' + response.body.id + '?tab=0')
                this.emitSuccess()
              })
          } else {
            window.open('/users/edit/' + response.body.id)
            this.emitSuccess()
          }
        }, this.onCreateError)
    },
    async createCourier() {
      this.saving = true
      let courier
      try {
        // The backend creates it pending Moova approval and assigns the courier role itself
        courier = (await UserService.createCourier({ name: this.newUser.name, email: this.newUser.email })).body
      } catch (error) {
        this.saving = false
        this.onCreateError(error)
        return
      }
      // The courier already exists from here on: failures keep going to step 2, where they can be retried
      const documentation = { card_id_number: this.documentNumber }
      this.documentationPrefill = await UserService.update(courier.id, documentation)
        .then(() => ({}), () => documentation)
      this.courier = await UserService.get(courier.id, COURIER_INCLUDES)
        .then(response => response.body, () => ({ ...courier, vehicles: [] }))
      this.saving = false
      this.step = 2
    },
    // The prefill holds a document number that could not be saved: only a documentation save clears it
    reloadCourier(clearPrefill) {
      UserService.get(this.courier.id, COURIER_INCLUDES)
        .then(response => {
          if (clearPrefill) this.documentationPrefill = {}
          this.courier = response.body
        })
    },
    onCreateError(error) {
      if (error.status !== 409) return
      const params = {
        filter_groups: [{
          filters: [
            ['email', 'eq', this.newUser.email]
          ]
        }]
      }
      // The 409 alert stays visible unless the lookup finds company users to point to
      UserService.getAll({ params, skipErrorAlert: true })
        .then(response => {
          this.existingUsers = response.body.data
          if (this.existingUsers.length > 0) {
            this.$root.$children[0].$refs.simplert.justCloseSimplert()
          }
        }, () => {})
    },
    emitSuccess() {
      this.$emit('success')
    },
    emitCancel() {
      this.$emit('cancel')
    }
  }
}
</script>
